import { fork, all } from 'redux-saga/effects';

import { simpleRequestsLoader } from 'modules/app/utils';
import {
  deleteCampaignFailure,
  deleteCampaignSuccess,
  patchCampaignStatusSuccess,
} from 'modules/campaigns/actions/core';

import { followCampaignSuccess, unfollowCampaignSuccess } from 'modules/campaigns/actions/details';
import * as types from 'modules/campaigns/actionTypes';

import dashboardSaga from './dashboard';
import detailsSaga from './details';
import sendCampaignSaga from './send';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.DUPLICATE_CAMPAIGN]: {
    notifications: {
      success: {
        id: 'page.campaigns.actions.duplicate.success',
        defaultMessage: 'The campaign has been duplicated with success.',
      },
      failure: {
        id: 'page.campaigns.actions.duplicate.failure',
        defaultMessage: 'An error occurred while duplicating the campaign.',
      },
    },
  },
  [types.CREATE_CAMPAIGN]: {
    notifications: {
      success: { id: 'page.campaigns.create.success', defaultMessage: 'The campaign has been created with success.' },
      failure: {
        id: 'page.campaigns.create.failure',
        defaultMessage: 'An error occurred during the campaign creation.',
      },
    },
  },
  [types.UPDATE_CAMPAIGN]: {
    notifications: {
      success: { id: 'page.campaigns.update.success', defaultMessage: 'The campaign has been updated with success.' },
      failure: { id: 'page.campaigns.update.failure', defaultMessage: 'An error occurred during the campaign update.' },
    },
  },
  [types.PATCH_CAMPAIGN_STATUS]: {
    callbacks: {
      success: (payload, meta, { payload: originalPayload }) =>
        patchCampaignStatusSuccess(payload, { ...meta, status: originalPayload.request?.data?.status }),
    },
    notifications: {
      success: (_, { payload, meta }) => {
        const { status } = payload.request.data;
        const translationField = meta?.unarchive ? 'unarchive' : status;
        return {
          id: `page.campaigns.patch.success.status.${translationField}`,
          defaultMessage: `The campaign has been ${status} with success.`,
        };
      },
      failure: (_, { payload, meta }) => {
        const { status } = payload.request.data;
        const translationField = meta?.unarchive ? 'unarchive' : status;
        return {
          id: `page.campaigns.patch.failure.status.${translationField}`,
          defaultMessage: `An error occurred during the campaign ${status}.`,
        };
      },
    },
  },
  [types.PATCH_CAMPAIGN_NAME]: {
    notifications: {
      success: {
        id: 'page.campaigns.patch.name.success',
        defaultMessage: 'The campaign name has been updated with success.',
      },
      failure: {
        id: 'page.campaigns.patch.name.failure',
        defaultMessage: 'An error occurred while editing the campaign name.',
      },
    },
  },
  [types.SEND_CAMPAIGN]: {
    notifications: {
      success: (_, { payload }) => {
        const { dryRun } = payload.request.data;
        const field = dryRun ? 'sendTest' : 'send';
        return {
          id: `page.campaigns.${field}.success`,
          defaultMessage: 'The campaign has been sent with success.',
        };
      },
      failure: (_, { payload }) => {
        const { dryRun } = payload.request.data;
        const field = dryRun ? 'sendTest' : 'send';
        return {
          id: `page.campaigns.${field}.failure`,
          defaultMessage: 'The campaign has been sent with success.',
        };
      },
    },
  },
  [types.DELETE_CAMPAIGN_SEND_DATE]: {
    notifications: {
      success: {
        id: 'page.campaigns.deleteSendDate.success',
        defaultMessage: 'The campaign has been unscheduled with success.',
      },
      failure: {
        id: 'page.campaigns.deleteSendDate.failure',
        defaultMessage: 'An error occurred while unscheduling the campaign.',
      },
    },
  },
  [types.PATCH_CAMPAIGN_SEND_DATE]: {
    notifications: {
      success: {
        id: 'page.campaigns.patchSendDate.success',
        defaultMessage: 'The campaign has been scheduled with success.',
      },
      failure: {
        id: 'page.campaigns.patchSendDate.failure',
        defaultMessage: 'An error occurred while scheduling the campaign.',
      },
    },
  },
  [types.DELETE_CAMPAIGN]: {
    callbacks: {
      success: (_, meta, { payload }) => deleteCampaignSuccess(payload.campaignId, meta),
      failure: (error, meta, { payload }) => deleteCampaignFailure(payload.campaignId, error, meta),
    },
    notifications: {
      success: { id: 'page.campaigns.delete.success', defaultMessage: 'The campaign has been deleted with success.' },
      failure: {
        id: 'page.campaigns.delete.failure',
        defaultMessage: 'An error occurred during the campaign deletion.',
      },
    },
  },
  [types.EXPORT_CAMPAIGN]: {
    notifications: {
      success: {
        id: 'page.campaigns.actions.export.success',
        defaultMessage: 'The campaign has been exported with success.',
      },
      failure: {
        id: 'page.campaigns.actions.export.failure',
        defaultMessage: 'An error occurred during the campaign export.',
      },
    },
  },
  [types.FOLLOW_CAMPAIGN]: {
    callbacks: {
      success: (_, __, { payload: originalPayload }) => followCampaignSuccess(originalPayload.campaignId),
    },
    notifications: {
      success: {
        id: 'page.campaigns.actions.follow.success',
        defaultMessage: 'The campaign follow operation is in progress.',
      },
      failure: {
        id: 'page.campaigns.actions.follow.failure',
        defaultMessage: 'An error occurred while following the campaign. Please try again later.',
      },
    },
  },
  [types.UNFOLLOW_CAMPAIGN]: {
    callbacks: {
      success: (_, __, { payload: originalPayload }) => unfollowCampaignSuccess(originalPayload.campaignId),
    },
    notifications: {
      success: {
        id: 'page.campaigns.actions.unfollow.success',
        defaultMessage: 'The campaign unfollow operation is in progress.',
      },
      failure: {
        id: 'page.campaigns.actions.unfollow.failure',
        defaultMessage: 'An error occurred while unfollowing the campaign. Please try again later.',
      },
    },
  },
};

export default function* campaignsSaga() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield fork(dashboardSaga);
  yield fork(sendCampaignSaga);
  yield fork(detailsSaga);
}
